import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { CalendarDays, Users, RefreshCw, ExternalLink } from "lucide-react";

function fmtDate(d) {
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "short" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
function isoDate(d) { return d.toISOString().substring(0, 10); }

export default function TechnikerKalender() {
  const [tab, setTab] = useState("kalender"); // kalender | techniker | teams
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startOffset, setStartOffset] = useState(0); // 0 = heute, +7 = nächste Woche

  const range = useMemo(() => {
    const start = new Date(); start.setDate(start.getDate() + startOffset); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 14);
    return { start, end };
  }, [startOffset]);

  const loadEvents = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/reonic/calendar-events", {
        params: { start_gt: range.start.toISOString(), start_lt: range.end.toISOString(), page: 0 },
      });
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.detail || "Konnte Reonic-Daten nicht laden.");
    } finally { setLoading(false); }
  };
  const loadUsers = async () => {
    try { const { data } = await api.get("/reonic/users"); setUsers(data || []); }
    catch (e) { setError(e.response?.data?.detail || "Fehler Users"); }
  };
  const loadTeams = async () => {
    try { const { data } = await api.get("/reonic/teams"); setTeams(data || []); }
    catch (e) { setError(e.response?.data?.detail || "Fehler Teams"); }
  };

  useEffect(() => { loadEvents(); }, [startOffset]);
  useEffect(() => { if (tab === "techniker" && users.length === 0) loadUsers(); }, [tab]);
  useEffect(() => { if (tab === "teams" && teams.length === 0) loadTeams(); }, [tab]);

  // Gruppieren nach Tag
  const days = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const d = new Date(e.start);
      const key = isoDate(d);
      if (!map.has(key)) map.set(key, { date: d, items: [] });
      map.get(key).items.push(e);
    }
    return [...map.values()].sort((a, b) => a.date - b.date);
  }, [events]);

  return (
    <div className="space-y-5" data-testid="techniker-kalender-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-falke-blue inline-flex items-center gap-2"><CalendarDays /> Techniker-Kalender</h1>
          <p className="text-falke-anthracite/70 mt-1">Live aus Reonic — Termine, Techniker und Teams.</p>
        </div>
        <button onClick={() => { setUsers([]); setTeams([]); loadEvents(); }} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="reonic-refresh"><RefreshCw size={14} /> Aktualisieren</button>
      </div>

      <div className="flex gap-2 p-1 bg-white rounded-xl border border-falke-blue/10 inline-flex" data-testid="kalender-tabs">
        {[["kalender", "Kalender"], ["techniker", "Techniker"], ["teams", "Teams"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === id ? "bg-falke-orange text-white" : "text-falke-blue/70 hover:bg-falke-orange/10"}`} data-testid={`tab-${id}`}>{l}</button>
        ))}
      </div>

      {error && <div className="falke-card p-4 text-rose-600 text-sm" data-testid="reonic-error">{error}</div>}

      {tab === "kalender" && (
        <>
          <div className="flex gap-2 items-center text-sm">
            <button onClick={() => setStartOffset(startOffset - 14)} className="btn-outline !py-1.5 text-xs" data-testid="cal-prev">← Vorherige 2 Wochen</button>
            <span className="text-falke-blue/70 tabular">{fmtDate(range.start)} – {fmtDate(new Date(range.end.getTime() - 86400000))}</span>
            <button onClick={() => setStartOffset(startOffset + 14)} className="btn-outline !py-1.5 text-xs" data-testid="cal-next">Nächste 2 Wochen →</button>
            {startOffset !== 0 && <button onClick={() => setStartOffset(0)} className="text-falke-orange text-xs hover:underline" data-testid="cal-heute">Heute</button>}
          </div>

          {loading ? (
            <div className="falke-card p-8 text-center text-falke-blue/60">Lade Termine …</div>
          ) : days.length === 0 ? (
            <div className="falke-card p-8 text-center text-falke-anthracite/60">
              <CalendarDays className="mx-auto opacity-30 mb-2" size={36} />
              Keine Termine in diesem Zeitraum.
            </div>
          ) : (
            <div className="space-y-4">
              {days.map((d) => (
                <div key={isoDate(d.date)} className="falke-card overflow-hidden">
                  <div className="bg-falke-blue/5 px-5 py-2.5 font-heading font-semibold text-falke-blue inline-flex items-center gap-2 w-full">
                    {fmtDate(d.date)} <span className="text-xs text-falke-anthracite/60 font-normal">· {d.items.length} Termin{d.items.length === 1 ? "" : "e"}</span>
                  </div>
                  <ul className="divide-y divide-falke-blue/10">
                    {d.items.map((e) => (
                      <li key={e.id} className="px-5 py-3 flex items-start gap-3" data-testid={`cal-event-${e.id}`}>
                        <div className="w-1.5 h-12 rounded-full flex-shrink-0" style={{ background: e.calendar?.color || "#2D5C7E" }} />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-falke-blue truncate">{e.title}</div>
                          <div className="text-xs text-falke-anthracite/60 mt-0.5">
                            {e.isAllDay ? "Ganztägig" : `${fmtTime(e.start)} – ${fmtTime(e.end)}`}
                            {e.organizer && <> · {e.organizer.firstName} {e.organizer.lastName}</>}
                            {e.location && <> · {e.location}</>}
                          </div>
                          {e.descriptionForCustomer && <div className="text-xs text-falke-anthracite/70 mt-1 line-clamp-2">{e.descriptionForCustomer}</div>}
                        </div>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md text-white font-bold" style={{ background: e.calendar?.color || "#2D5C7E" }}>{e.calendar?.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "techniker" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {users.map((u) => (
            <div key={u.id} className="falke-card p-4 flex items-center gap-3" data-testid={`reonic-user-${u.id}`}>
              {u.logoUrl ? <img src={u.logoUrl} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-falke-blue/10 flex items-center justify-center text-falke-blue font-bold">{(u.firstName || u.fullName || "?")[0]}</div>}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-falke-blue truncate">{u.fullName || `${u.firstName} ${u.lastName}`}</div>
                <div className="text-xs text-falke-anthracite/60 truncate">{u.email}</div>
                <div className="text-[10px] text-falke-orange uppercase tracking-wider mt-0.5">{u.role}</div>
              </div>
              {u.phoneNumber && <a href={`tel:${u.phoneNumber}`} className="text-falke-blue/70 hover:text-falke-orange"><ExternalLink size={14} /></a>}
            </div>
          ))}
          {users.length === 0 && !error && <div className="text-falke-blue/60 text-sm">Lade Techniker …</div>}
        </div>
      )}

      {tab === "teams" && (
        <div className="grid sm:grid-cols-2 gap-3">
          {teams.map((t) => (
            <div key={t.id} className="falke-card p-5" data-testid={`reonic-team-${t.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Users size={16} className="text-falke-orange" />
                <div className="font-heading font-semibold text-falke-blue">{t.name}</div>
              </div>
              {t.description && <div className="text-xs text-falke-anthracite/70 mb-2">{t.description}</div>}
              <div className="text-xs text-falke-anthracite/65">{(t.users || []).length} Mitglied{(t.users || []).length === 1 ? "" : "er"}</div>
            </div>
          ))}
          {teams.length === 0 && !error && <div className="text-falke-blue/60 text-sm">Lade Teams …</div>}
        </div>
      )}
    </div>
  );
}
